/**
 * Instantly place a widget at the next available canvas position.
 * Mirrors the pattern of useCanvasAddContextPlacement for entities —
 * no placement mode required, no canvas click needed.
 */

import type { WidgetPreset } from "@/pages/canvas/nodes/widgets";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasNodePlacement } from "./useCanvasNodePlacement";

export function useCanvasWidgetQuickPlace() {
  const { addNode } = useCanvas();
  const { getNextNodePosition } = useCanvasNodePlacement();

  const placeWidget = useCallback(
    (preset: WidgetPreset, flowPosition?: { x: number; y: number }) => {
      const position =
        flowPosition ??
        getNextNodePosition(preset.defaultWidth, preset.defaultHeight);

      addNode({
        id: `widget-${uuidv4()}`,
        type: "widgetNode",
        position,
        width: preset.defaultWidth,
        // Fill-mode widgets (e.g. retro board, scaleContent: false) need an
        // explicit height so w-full/h-full fills correctly from the start.
        // Scale-mode widgets (Timer, Music) omit height so React Flow measures
        // the DOM first — ReactWidgetShell then syncs the real height to Loro.
        ...(preset.resizable?.scaleContent === false
          ? { height: preset.defaultHeight }
          : {}),
        ...(preset.zIndex !== undefined && { zIndex: preset.zIndex }),
        data: {
          ...(preset.component ? {} : { html: preset.html }),
          label: preset.label,
          presetId: preset.id,
          ...(preset.initialData ?? {}),
        },
      } as Parameters<typeof addNode>[0]);
    },
    [getNextNodePosition, addNode],
  );

  return { handleQuickPlaceWidget: placeWidget, placeWidget };
}
